import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Panel } from 'primereact/panel';
import { defineMessages, useIntl } from 'react-intl';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';

import ArrayUtil from '../../../utils/ArrayUtil';
import Paginator from '../../../components/Paginator';
import { toSortOrder, sortUrl, parse } from '../../../utils/UrlUtil';
import AssessmentTemplateListHeader from './AssessmentTemplateListHeader';
import { useLocation, useHistory } from 'react-router-dom';
import { AccountContext } from '../../../components/AccountContext';
import { NotificationContext } from '../../../components/GrowlNotifications';
import useSubscriptionExpiredRestrictions from '../../../hooks/useSubscriptionExpiredRestrictions';

const messages = defineMessages({
  success: { id: 'noti.success', defaultMessage: 'Success!' },
  title: { id: 'temp.title', defaultMessage: 'Title' },
  dimensions: { id: 'attrs.dimension.plural', defaultMessage: 'Dimensions' },
  updated: { id: 'attrs.updated', defaultMessage: 'Updated' },
  created: { id: 'attrs.created', defaultMessage: 'Created' },
  action: { id: 'attrs.actions', defaultMessage: 'Actions' },
});

const dimensionCount = (behavior, cognitive, interest) => {
  const behaviorTotal = ArrayUtil.isNotEmpty(behavior) ? behavior.length : 0;
  const cognitiveTotal = ArrayUtil.isNotEmpty(cognitive) ? cognitive.length : 0;
  const interestTotal = ArrayUtil.isNotEmpty(interest) ? interest.length : 0;
  return behaviorTotal + cognitiveTotal + interestTotal;
};

const nameTemplate = (rowData, { currentAccount, keyField }) => {
  const detailUrl = `/accounts/${currentAccount}/assessments/templates/${rowData[keyField]}`;
  return (
    <Link to={detailUrl} className="app-link">
      {rowData.title}
    </Link>
  );
};

const timeFieldTemplate = (rowData, { formatDate, field }) => {
  const updated = formatDate(moment(rowData[field]), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
  return updated;
};

const AssessmentTemplateList = ({
  connection: { edges, totalCount, indexFrom, indexTo, pageInfo },
  deleteTemplate,
  loading,
}) => {
  const { formatMessage, formatDate } = useIntl();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const location = useLocation();
  const history = useHistory();
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();

  const onSortChange = (event) => {
    sortUrl(event, location, history);
  };

  const actionsTemplate = (rowData, props) => {
    const { canDelete } = rowData;

    return (
      <Button
        icon="pi pi-times"
        className="p-button-secondary"
        disabled={!canDelete || subscriptionIsExpired}
        onClick={() => handleDeleteTemplate(rowData.id, props)}
      />
    );
  };

  const handleDeleteTemplate = async (id, props) => {
    const { deleteTemplate, currentAccount, growl, formatMessage } = props;

    try {
      await deleteTemplate({
        variables: {
          input: {
            accountId: currentAccount,
            assessmentTemplateId: id,
          },
        },
      });
      growl.show({ severity: 'info', detail: formatMessage(messages.success) });
    } catch (err) {
      growl.show({ severity: 'error', sticky: true, detail: err.message });
    }
  };

  const params = parse(location.search);
  const { sort = {} } = params;
  const { sortBy, sortDir } = sort;

  // data list
  const listRows = edges.map(({ node }) => ({
    ...node,
    totalDimension: dimensionCount(node.behaviorDimensions, node.cognitiveDimensions, node.interestsDimensions),
  }));

  // header
  const titleHeader = formatMessage(messages.title);
  const dimCountHeader = formatMessage(messages.dimensions);
  const updatedHeader = formatMessage(messages.updated);
  const createdHeader = formatMessage(messages.created);
  const actionHeader = formatMessage(messages.action);
  // footer
  const footer = <Paginator totalCount={totalCount} indexFrom={indexFrom} indexTo={indexTo} pageInfo={pageInfo} />;

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Panel>
            <div className="p-grid">
              <div className="p-col-12">
                <AssessmentTemplateListHeader currentAccount={currentAccount} />
              </div>
              <div className="p-col-12 align-center">
                <DataTable
                  value={listRows}
                  responsive
                  sortField={sortBy}
                  sortOrder={toSortOrder(sortDir)}
                  onSort={onSortChange}
                  lazy
                  loading={loading}
                  autoLayout
                >
                  <Column
                    field="title"
                    header={titleHeader}
                    body={nameTemplate}
                    keyField="id"
                    currentAccount={currentAccount}
                    sortable
                    style={{ textAlign: 'left' }}
                  />
                  <Column field="totalDimension" header={dimCountHeader} />
                  <Column
                    field="createdOn"
                    body={timeFieldTemplate}
                    header={createdHeader}
                    formatDate={formatDate}
                    sortable
                  />
                  <Column
                    field="updatedOn"
                    header={updatedHeader}
                    body={timeFieldTemplate}
                    formatDate={formatDate}
                    sortable
                  />
                  <Column
                    header={actionHeader}
                    body={actionsTemplate}
                    deleteTemplate={deleteTemplate}
                    currentAccount={currentAccount}
                    growl={growl}
                    formatMessage={formatMessage}
                    style={{ width: '3.5em' }}
                  />
                </DataTable>
              </div>
              <div className="p-col-12 align-center">{footer}</div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};

AssessmentTemplateList.propTypes = {
  connection: PropTypes.objectOf(PropTypes.any).isRequired,
  loading: PropTypes.bool.isRequired,
  deleteTemplate: PropTypes.func.isRequired,
};

export default AssessmentTemplateList;
